import { Directive, ElementRef, HostListener, OnInit, Optional, Renderer2, Self } from '@angular/core';
import { NgControl } from '@angular/forms';
import { sanitizePlainTextValue } from 'avn/modules/shared/directives/input-safety.validator';

const UNSAFE_TEXT_INPUT_SKIP_TYPES = [
  'checkbox',
  'radio',
  'file',
  'password',
  'hidden',
  'range',
  'color',
  'submit',
  'button',
  'reset',
  'number',
  'date',
  'datetime-local',
  'month',
  'time',
  'week'
];

const SELECTION_RANGE_SUPPORTED_INPUT_TYPES = ['text', 'search', 'tel', 'url', 'password'];

@Directive({ selector: 'input, textarea, [appInputSafety]' })
export class InputSafetyDirective implements OnInit {
  public constructor(
    private readonly elementRef: ElementRef<HTMLInputElement | HTMLTextAreaElement>,
    private readonly renderer: Renderer2,
    @Optional() @Self() private readonly ngControl?: NgControl
  ) {}

  public ngOnInit(): void {
    this.applySanitization(false);
  }

  @HostListener('input')
  public onInput(): void {
    this.applySanitization(false);
  }

  @HostListener('blur')
  public onBlur(): void {
    this.applySanitization(true);
  }

  @HostListener('paste', ['$event'])
  public onPaste(event: Event): void {
    const pastedText = (event as ClipboardEvent).clipboardData?.getData('text');
    if (pastedText === undefined) {
      return;
    }

    const sanitizedText = sanitizePlainTextValue(pastedText);
    if (sanitizedText === pastedText) {
      return;
    }

    event.preventDefault();
    this.insertSanitizedText(sanitizedText);
    this.dispatchInputEvent();
  }

  private applySanitization(trim: boolean): void {
    if (!this.shouldSanitize()) {
      return;
    }

    const input = this.elementRef.nativeElement;
    const originalValue = input.value ?? '';
    const sanitizedValue = sanitizePlainTextValue(originalValue, trim);

    if (sanitizedValue === originalValue) {
      return;
    }

    const cursorStart = input.selectionStart ?? sanitizedValue.length;
    this.setElementAndControlValue(sanitizedValue);

    const nextCursorPosition = Math.min(cursorStart, sanitizedValue.length);
    this.setCursorPosition(nextCursorPosition);
  }

  private insertSanitizedText(text: string): void {
    if (!this.shouldSanitize()) {
      return;
    }

    const input = this.elementRef.nativeElement;
    const currentValue = input.value ?? '';
    const selectionStart = input.selectionStart ?? currentValue.length;
    const selectionEnd = input.selectionEnd ?? currentValue.length;
    const nextValue = currentValue.slice(0, selectionStart) + text + currentValue.slice(selectionEnd);

    this.setElementAndControlValue(nextValue);

    const cursorPosition = selectionStart + text.length;
    this.setCursorPosition(cursorPosition);
  }

  private setElementAndControlValue(value: string): void {
    this.renderer.setProperty(this.elementRef.nativeElement, 'value', value);
    this.ngControl?.control?.setValue(value, { emitEvent: true, emitModelToViewChange: false });
  }

  private dispatchInputEvent(): void {
    this.elementRef.nativeElement.dispatchEvent(new Event('input', { bubbles: true }));
  }

  private setCursorPosition(cursorPosition: number): void {
    const element = this.elementRef.nativeElement;

    if (element instanceof HTMLInputElement) {
      const inputType = (element.type || element.getAttribute('type') || 'text').toLowerCase();
      if (!SELECTION_RANGE_SUPPORTED_INPUT_TYPES.includes(inputType)) {
        return;
      }
    }

    if (typeof element.setSelectionRange === 'function') {
      element.setSelectionRange(cursorPosition, cursorPosition);
    }
  }

  private shouldSanitize(): boolean {
    const element = this.elementRef.nativeElement;
    const tagName = element.tagName.toLowerCase();

    if (element.readOnly || element.disabled) {
      return false;
    }

    if (tagName === 'textarea') {
      return true;
    }

    if (tagName !== 'input') {
      return false;
    }

    const input = element as HTMLInputElement;
    const inputType = (input.type || input.getAttribute('type') || 'text').toLowerCase();
    const passwordLikeIdentifier = [input.name, input.id, input.autocomplete, input.getAttribute('formcontrolname')]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return !UNSAFE_TEXT_INPUT_SKIP_TYPES.includes(inputType) && !passwordLikeIdentifier.includes('password');
  }
}
